import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User as UserIcon, Mail, Calendar, Shield, Lock, Globe, FileText, Save, Edit2, X, Info, Bot } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useSkillsStore } from '../store/skillsStore';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Modal from '../components/ui/Modal';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';
import { countries, getCountryName } from '../data/countries';

const formatDate = (dateString?: string) => {
  if (!dateString) return null;

  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.warn('Failed to format date:', error);
    return null;
  }
};

const Profile: React.FC = () => {
  const { user, setUser } = useAuthStore();
  const { skills, fetchSkills } = useSkillsStore();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [isSendingResetLink, setIsSendingResetLink] = React.useState(false);
  const [formData, setFormData] = React.useState({
    display_name: user?.display_name || '',
    country: user?.country || '',
    description: user?.description || '',
    default_skill_id: user?.default_skill_id || 'default',
  });

  // Fetch skills on mount
  React.useEffect(() => {
    if (skills.length === 0) {
      fetchSkills();
    }
  }, [skills.length, fetchSkills]);

  const joinedDate = formatDate(user?.created_at);

  React.useEffect(() => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        country: user.country || '',
        description: user.description || '',
        default_skill_id: user.default_skill_id || 'default',
      });
    }
  }, [user]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedUser = await apiService.updateUserProfile(formData);
      setUser(updatedUser);
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        display_name: user.display_name || '',
        country: user.country || '',
        description: user.description || '',
        default_skill_id: user.default_skill_id || 'default',
      });
    }
    setIsEditing(false);
  };

  const handleSendPasswordResetLink = async () => {
    if (!user?.email) return;
    setIsSendingResetLink(true);
    try {
      await apiService.forgotPassword(user.email);
      toast.success('Password reset link sent to your email');
      setShowPasswordModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to send reset link');
    } finally {
      setIsSendingResetLink(false);
    }
  };

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
            <UserIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Profile unavailable</h2>
            <p className="text-sm text-gray-600">We couldn't load your profile details. Please sign in again.</p>
          </div>
          <Button onClick={() => navigate('/login')} variant="primary">
            Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 py-4 sm:py-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
            <p className="mt-1 text-xs text-gray-600">
              Manage your personal information and account settings.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {isEditing ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  icon={X}
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  icon={Save}
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={Edit2}
                onClick={() => setIsEditing(true)}
              >
                Edit
              </Button>
            )}
          </div>
        </div>

        <div className="grid gap-3">
          <section className="rounded-lg bg-white shadow-sm ring-1 ring-gray-100">
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center space-x-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <UserIcon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  {isEditing ? (
                    <Input
                      type="text"
                      value={formData.display_name}
                      onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                      placeholder="Display name"
                      className="w-full max-w-xs"
                    />
                  ) : (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {user.display_name || user.email || 'User'}
                      </h2>
                      <p className="text-xs text-gray-500">
                        {user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Member'}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  icon={Lock}
                  onClick={() => setShowPasswordModal(true)}
                >
                  Password
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/settings')}
                >
                  Settings
                </Button>
              </div>
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Mail className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Email</h3>
                  <p className="mt-0.5 text-sm font-medium text-gray-900 break-all">{user.email}</p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Calendar className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide">Member Since</h3>
                  <p className="mt-0.5 text-sm font-medium text-gray-900">
                    {joinedDate || 'Not available'}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                  <Globe className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Country</h3>
                  {isEditing ? (
                    <Select
                      value={formData.country || ''}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      placeholder="Select country"
                      options={countries.map((c) => ({ value: c.name, label: c.name }))}
                      icon={Globe}
                      iconPosition="left"
                      className="w-full text-sm"
                    />
                  ) : (
                    <p className="text-sm font-medium text-gray-900">
                      {user.country || 'Not specified'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100">
              <div className="flex items-start space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                  <Bot className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">Default Agent</h3>
                  {isEditing ? (
                    <div>
                      <Select
                        value={formData.default_skill_id || 'default'}
                        onChange={(e) => setFormData({ ...formData, default_skill_id: e.target.value })}
                        placeholder="Select agent"
                        options={skills.map(skill => ({
                          value: skill.skill_id,
                          label: skill.name
                        }))}
                        icon={Bot}
                        iconPosition="left"
                        className="w-full text-sm"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Used for new conversations. Changeable per thread.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {skills.find(s => s.skill_id === user.default_skill_id)?.name || 
                         skills.find(s => s.is_default)?.name || 
                         'Default Assistant'}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">
                        {skills.find(s => s.skill_id === user.default_skill_id)?.description || 
                         'Generic AI assistant'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-100 sm:col-span-2">
              <div className="flex items-start space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">About Me</h3>
                  {isEditing ? (
                    <div>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        placeholder="Tell us about yourself..."
                        rows={3}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                      />
                      <div className="mt-1.5 flex items-start space-x-1.5 text-xs text-gray-600 bg-blue-50 border border-blue-200 rounded p-2">
                        <Info className="h-3.5 w-3.5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p>
                          Helps AI personalize responses to your background and profession.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-900 whitespace-pre-wrap line-clamp-3">
                      {user.description || 'No description provided'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Change password modal: send reset link to user's email */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Change password"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            We&apos;ll send a password reset link to <strong className="text-gray-900">{user?.email}</strong>. 
            Open the link in your browser to set a new password.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPasswordModal(false)}
              disabled={isSendingResetLink}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSendPasswordResetLink}
              loading={isSendingResetLink}
              disabled={isSendingResetLink}
            >
              Send reset link
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Profile;

